// ===================================================================
// IndividualPromptGenerator.jsx
// DIVIDE Y VENCERÁS — 11 Prompts individuales Cambridge-quality
//
// INSTRUCCIONES DE INTEGRACIÓN:
// 1. Copia este archivo completo en src/components/ (o donde tengas los componentes)
// 2. En CourseCmsTab.jsx, busca el bloque "SPACY PROMPT GENERATOR" (la segunda
//    tarjeta del RIGHT COL que empieza con <div style={{background:t.bg...}}>
//    y contiene el Brain icon + "Master Prompt · Anti-Alucinaciones v3")
// 3. Reemplaza ESE div completo con:
//    <IndividualPromptGenerator
//      t={t} dark={dark} HICOLOR={HICOLOR}
//      lessonId={currentLessonId}
//      lessonTopic={lessonTopic}
//      setLessonTopic={setLessonTopic}
//      activeLevel={activeLevel}
//    />
// 4. Agrega el import al inicio de CourseCmsTab.jsx:
//    import IndividualPromptGenerator from '../components/IndividualPromptGenerator';
// ===================================================================

import { useState, useCallback } from "react";
import { Brain, Copy, Check, Terminal, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

// ── Metadatos de cada ejercicio ────────────────────────────────────
const EX_TYPE_META = {
  conversation_spacy_prompt: { label: "Spacy Prompt",    color: "#00FFFF", emoji: "🤖", pos: 1  },
  grammar_notes:             { label: "Grammar Notes",   color: "#6366f1", emoji: "📖", pos: 2  },
  quiz:                      { label: "Quiz",            color: "#06b6d4", emoji: "❓", pos: 3  },
  fill_in_the_blank:         { label: "Fill in Blank",   color: "#10b981", emoji: "✏️", pos: 4  },
  matching:                  { label: "Matching",        color: "#f59e0b", emoji: "🔗", pos: 5  },
  listening:                 { label: "Listening",       color: "#ec4899", emoji: "🎧", pos: 6  },
  pronunciation:             { label: "Pronunciation",   color: "#f97316", emoji: "🎙️", pos: 7  },
  error_detection:           { label: "Error Detection", color: "#ef4444", emoji: "🔍", pos: 8  },
  reading:                   { label: "Reading",         color: "#8b5cf6", emoji: "📚", pos: 9  },
  writing:                   { label: "Writing",         color: "#14b8a6", emoji: "✍️", pos: 10 },
  spacy_mirror:              { label: "Spacy Mirror",    color: "#00FFFF", emoji: "🧠", pos: 11 },
};

const EX_ORDER = [
  "conversation_spacy_prompt",
  "grammar_notes",
  "quiz",
  "fill_in_the_blank",
  "matching",
  "listening",
  "pronunciation",
  "error_detection",
  "reading",
  "writing",
  "spacy_mirror",
];

// ===================================================================
// GENERADORES DE PROMPTS — Uno por tipo, Cambridge-quality
// ===================================================================

const buildPrompt = (type, { topic, grammarPoint, level, lessonId }) => {
  const gp = grammarPoint || "[DEFINE EL PUNTO GRAMATICAL CENTRAL]";
  const t  = topic        || "[TEMA DE LA LECCIÓN]";
  const lv = level        || "A1";
  const id = lessonId     || "[lesson_id]";

  const HEADER = `Eres un Diseñador Instruccional Senior certificado Cambridge English (CELTA/DELTA).
PUNTO GRAMATICAL CENTRAL: "${gp}"
TEMA CONTEXTO: "${t}"
NIVEL CEFR: ${lv}
lesson_id: ${id}

⚠️ REGLA ABSOLUTA: Devuelve SOLO el objeto JSON del campo "data". Sin texto antes ni después. Sin markdown. Sin bloques de código. Solo el JSON puro del objeto data.

`;

  const prompts = {

    // ── 1. SPACY PROMPT ─────────────────────────────────────────────
    conversation_spacy_prompt: HEADER + `GENERA el "data" para un ejercicio conversation_spacy_prompt.

Este es el PLAN DE CLASE del tutor AI Spacy para enseñar "${gp}" en el contexto de "${t}".

ESTRUCTURA REQUERIDA:
{
  "lesson_introduction": "Hello [Student]! I'm Spacy. Today we'll master [gp en 1 frase]. Ready? Let's go! ((¡Hola! Hoy dominaremos [gp]. ¡Empecemos!))",
  "learning_objectives": [array de 7-8 strings que son las FASES de la clase, SIEMPRE termina con "Lección Completada 🎓"],
  "visual_gallery_cards": [array de 2-3 tarjetas visuales]
}

REGLAS PARA learning_objectives:
- Fase 1: Diagnóstico y calentamiento
- Fase 2: Presentación de "${gp}" con ejemplo real
- Fase 3: Práctica controlada (forma)
- Fase 4: Práctica libre (significado en contexto "${t}")
- Fase 5: Juego de roles o escenario real
- Fase 6: Corrección de errores típicos de hispanohablantes
- Fase 7: Autoevaluación
- Fase 8: "Lección Completada 🎓"

REGLAS PARA visual_gallery_cards (cada tarjeta):
{
  "trigger_topic": "[string del learning_objective que la activa]",
  "card_id": "V[N]-[Nombre]",
  "content": "[contenido de la tarjeta: regla, diagrama textual, ❌✅ ejemplos]"
}

Tarjeta 1: Diagrama de estructura de "${gp}" — [Sujeto] + [auxiliar] + [verbo] + ✅ / ❌
Tarjeta 2: Los 3 errores más comunes de hispanohablantes con "${gp}"
Tarjeta 3 (opcional): Vocabulario clave del contexto "${t}"

DEVUELVE SOLO EL OBJETO data.`,

    // ── 2. GRAMMAR NOTES ─────────────────────────────────────────────
    grammar_notes: HEADER + `GENERA el "data" para un ejercicio grammar_notes.

Esta es la MASTERCLASS CAMBRIDGE sobre "${gp}" en el contexto "${t}".

ESTRUCTURA REQUERIDA:
{
  "title": "[Emoji relevante] Masterclass: [Nombre formal de la estructura gramatical] en ${t}",
  "points": [array de 4-5 points]
}

CADA POINT tiene: { "header": "...", "content": "..." }

POINT 1 — "🧠 La Regla de Oro: [Nombre de la Estructura]"
  content DEBE incluir:
  - Estructura con corchetes: [Sujeto] + [verbo] + [complemento]
  - 1 ejemplo afirmativo con **negritas** en la estructura clave
  - 1 ejemplo negativo con **negritas**
  - Contraste con el español si hay falsa equivalencia (⚠️)

POINT 2 — "⚡ Cuándo Usarlo: Función Comunicativa"
  content DEBE incluir:
  - 2-3 funciones distintas separadas con ▸
  - Para cada función: 1 ejemplo EN CONTEXTO de "${t}" con traducción entre paréntesis
  - Ejemplos EN LISTAS VERTICALES con →

POINT 3 — "🔬 El Detalle que Cambia Todo"
  content DEBE incluir para nivel ${lv}:
  - A1-A2: excepción ortográfica o fonológica más común
  - B1-B2: contraste con forma similar (tabla | Forma ✅ | Forma ❌ | Por qué |)
  - C1-C2: matiz de registro formal vs informal
  - Verbos irregulares, excepciones, colocaciones fijas relevantes

POINT 4 — "🚫 Errores de Hispanohablantes (Interferencia L1)"
  content DEBE incluir EXACTAMENTE 3 errores en este formato:
  ❌ "[Oración con el error real que comete un hispanohablante]"
  ✅ "[Corrección exacta]"
  💡 "[Por qué ocurre: interferencia del español en 1 frase]"

POINT 5 (si nivel B1+) — "🚀 Nivel Pro: Matices de Registro"
  content: formal vs informal, hablado vs escrito, tip de pronunciación/entonación

PROHIBIDO:
- Listas de vocabulario sin gramática
- Points vagos tipo "Practica mucho"
- Mezclar dos puntos gramaticales sin conexión

DEVUELVE SOLO EL OBJETO data.`,

    // ── 3. QUIZ ──────────────────────────────────────────────────────
    quiz: HEADER + `GENERA el "data" para un ejercicio quiz.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "[Título motivador sobre ${t}]",
  "instruction": "Selecciona la opción gramaticalmente correcta según lo aprendido sobre ${gp}.",
  "xp": 20,
  "questions": [array de EXACTAMENTE 6 questions]
}

CADA QUESTION:
{
  "id": "q[N]",
  "question": "[Pregunta clara que requiere usar ${gp}]",
  "options": ["Opción A", "Opción B", "Opción C"],
  "answer": "[TEXTO EXACTO de una de las opciones — NUNCA número]"
}

REGLAS CRÍTICAS:
🚨 "answer" = TEXTO LITERAL de una opción. NUNCA "0", "1", "2" ni índice.
🚨 Las opciones INCORRECTAS deben ser errores REALES que comete un hispanohablante (no absurdos).
🚨 Varía la posición de la respuesta correcta (no siempre la primera).
🚨 Las 6 preguntas deben cubrir: afirmativo, negativo, pregunta, tiempo/contexto, error común, aplicación real en "${t}".

DEVUELVE SOLO EL OBJETO data.`,

    // ── 4. FILL IN THE BLANK ─────────────────────────────────────────
    fill_in_the_blank: HEADER + `GENERA el "data" para un ejercicio fill_in_the_blank.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Completa: [Título creativo relacionado a ${t}]",
  "instruction": "Escribe la forma correcta de ${gp} para completar cada oración. Lee las pistas si las necesitas.",
  "xp": 15,
  "blanks": [array de EXACTAMENTE 6 blanks]
}

CADA BLANK:
{
  "id": "b[N]",
  "before_blank": "[Texto ANTES del espacio en blanco]",
  "after_blank": "[Texto DESPUÉS del espacio + (infinitivo del verbo entre paréntesis si aplica)]",
  "answer": "[respuesta exacta en minúsculas]",
  "alternate_answer": "[variante aceptable o igual que answer]",
  "hint": "[Regla gramatical específica en español — QUÉ forma usar y POR QUÉ]",
  "emoji": "[emoji relacionado al contenido]"
}

REGLAS:
- Los 6 blanks deben cubrir variaciones de ${gp}: afirmativo, negativo, pregunta, 3ra persona, etc.
- before_blank y after_blank juntos deben formar una oración completa con sentido.
- El contexto de las oraciones debe ser "${t}".
- hint debe explicar la REGLA, no dar la respuesta directamente.

DEVUELVE SOLO EL OBJETO data.`,

    // ── 5. MATCHING ──────────────────────────────────────────────────
    matching: HEADER + `GENERA el "data" para un ejercicio matching.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Empareja: Forma y Función de ${gp}",
  "instruction": "Une cada elemento de la columna izquierda con su correspondiente de la derecha.",
  "xp": 50,
  "pairs": [5 objetos {id, content}],
  "matches": [5 objetos {id, content}],
  "solution": {objeto con pairN: matchN}
}

PAIRS (columna izquierda) — una mezcla de:
- Oraciones usando ${gp} en diferentes contextos de "${t}"
- Formas de la estructura (afirmativa, negativa, pregunta)
- Marcadores temporales o palabras clave

MATCHES (columna derecha) — una mezcla de:
- Función comunicativa de la oración del par
- Traducción al español
- Tipo de uso (formal/informal, afirmativo/negativo)
- Sinónimos o equivalentes

SOLUTION: {"pair1":"match3","pair2":"match5","pair3":"match1","pair4":"match4","pair5":"match2"}
🚨 NUNCA hagas solución 1→1 (pair1:match1). SIEMPRE mezcla el orden.
🚨 Todos los IDs de solution DEBEN existir en pairs y matches.
🚨 El número de entradas en solution DEBE ser igual al número de pairs.

DEVUELVE SOLO EL OBJETO data.`,

    // ── 6. LISTENING ─────────────────────────────────────────────────
    listening: HEADER + `GENERA el "data" para un ejercicio listening.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Listening: [Título evocador — situación real de ${t}]",
  "instruction": "Escucha el audio con atención. Presta especial atención al uso de ${gp}. Luego responde las preguntas.",
  "xp": 50,
  "audio_url": "https://res.cloudinary.com/PLACEHOLDER/audio_${(topic || "lesson").replace(/\s+/g,"_").toLowerCase()}_${lv.toLowerCase()}.mp3",
  "timeLimit": 180,
  "transcript": "[DIÁLOGO COMPLETO — ver reglas abajo]",
  "questions": [array de EXACTAMENTE 6 questions]
}

REGLAS PARA transcript:
- 3-4 turnos de conversación NATURAL entre 2 personas (nombradas)
- El diálogo DEBE situarse en el contexto "${t}"
- DEBE usar ${gp} al menos 3 veces de forma natural (no forzada)
- Nivel de vocabulario: ${lv}
- Formato: "Nombre: texto del diálogo"
- Longitud: 8-12 líneas

CADA QUESTION (igual que quiz):
{
  "id": "q[N]",
  "question": "[Pregunta de comprensión auditiva]",
  "options": ["A", "B", "C", "D"],
  "answer": "[TEXTO EXACTO de una opción]"
}

Las 6 preguntas cubren: ¿quién?, ¿qué?, ¿cómo/cuándo?, uso de ${gp} en el diálogo, inferencia, conclusión.

DEVUELVE SOLO EL OBJETO data.`,

    // ── 7. PRONUNCIATION ─────────────────────────────────────────────
    pronunciation: HEADER + `GENERA el "data" para un ejercicio pronunciation.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Pronunciación: Sonidos de ${gp} en ${t}",
  "instruction": "Escucha cuidadosamente, repite en voz alta y grábate. Compara con el modelo nativo. Presta atención a los sonidos marcados.",
  "xp": 40,
  "words_or_sentences": [array de EXACTAMENTE 10 items]
}

CADA ITEM:
{
  "text": "[palabra u oración]",
  "phonetic": "/[transcripción IPA correcta]/"
}

DISTRIBUCIÓN DE LOS 10 ITEMS:
- Items 1-3: Palabras clave individuales de ${gp} (ej: auxiliares, terminaciones)
- Items 4-6: Frases cortas (3-4 palabras) usando ${gp}
- Items 7-9: Oraciones completas en contexto "${t}" usando ${gp}
- Item 10: Oración desafiante con contraste fonológico o reducción de sonidos típica del inglés hablado

REGLAS IPA:
- IPA correcto con barras // alrededor
- Marca el acento primario con ˈ
- Para formas reducidas en habla natural: usa ə (schwa)
- Para contraction: 'I've /aɪv/', 'doesn't /ˈdʌznt/'

DEVUELVE SOLO EL OBJETO data.`,

    // ── 8. ERROR DETECTION ───────────────────────────────────────────
    error_detection: HEADER + `GENERA el "data" para un ejercicio error_detection.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Detective Gramatical: Correcto o Incorrecto",
  "instruction": "Lee cada oración con atención. Decide si es correcta o tiene un error gramatical relacionado con ${gp}. Si es incorrecta, la explicación te dirá por qué.",
  "xp": 20,
  "sentences": [array de EXACTAMENTE 7 sentences]
}

CADA SENTENCE:
{
  "id": "e[N]",
  "sentence": "[Oración en inglés]",
  "options": ["Correct", "Incorrect"],
  "answer": "Correct" o "Incorrect",
  "explanation": "[Si Incorrect: Error: [qué está mal]. Correcto: '[versión correcta]'. Regla: [por qué — en español]. | Si Correct: Correcto porque [razón gramatical breve].]"
}

DISTRIBUCIÓN:
- 3 oraciones CORRECTAS (con trampas que parecen incorrectas)
- 4 oraciones INCORRECTAS con errores REALES de hispanohablantes con ${gp}

🚨 "answer" SOLO puede ser exactamente "Correct" o "Incorrect" — con C e I MAYÚSCULAS.
🚨 Las oraciones incorrectas deben usar el contexto "${t}".
🚨 Los errores deben ser interferencia del español, no errores absurdos.

DEVUELVE SOLO EL OBJETO data.`,

    // ── 9. READING ───────────────────────────────────────────────────
    reading: HEADER + `GENERA el "data" para un ejercicio reading.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "Lectura: [Título periodístico o narrativo sobre ${t}]",
  "instruction": "Lee el texto con atención. Las estructuras de ${gp} están en **negrita**. Responde las preguntas de comprensión.",
  "xp": 25,
  "text": "[TEXTO COMPLETO — ver reglas]",
  "image_url": "https://images.unsplash.com/photo-PLACEHOLDER?w=800",
  "comprehension_quiz": {
    "questions": [array de EXACTAMENTE 6 questions]
  }
}

REGLAS PARA text:
- Mínimo 180 palabras, máximo 280 palabras
- Nivel de vocabulario: ${lv}
- Género: narrativo/descriptivo/informativo (elige el más apropiado para "${t}")
- DEBE usar ${gp} mínimo 4 veces de forma natural — marcadas con **negritas**
- Incluye detalles específicos que se preguntarán en el quiz (nombres, fechas, cantidades, lugares)
- El texto debe ser interesante y relevante para un estudiante de inglés de 2026

CADA QUESTION en comprehension_quiz:
{
  "id": "rq[N]",
  "question": "[Pregunta de comprensión lectora]",
  "options": ["A", "B", "C"],
  "answer": "[TEXTO EXACTO de una opción]"
}

Las 6 preguntas: 2 de detalle explícito, 2 de inferencia, 1 sobre uso de ${gp} en el texto, 1 de vocabulario en contexto.

DEVUELVE SOLO EL OBJETO data.`,

    // ── 10. WRITING ──────────────────────────────────────────────────
    writing: HEADER + `GENERA el "data" para un ejercicio writing.

TEMA: "${gp}" en contexto "${t}" | NIVEL ${lv}

ESTRUCTURA REQUERIDA:
{
  "title": "[Título inspirador que motive a escribir sobre ${t}]",
  "instruction": "[Instrucción detallada — ver reglas]",
  "xp": 50,
  "theme": "[Situación real y motivadora — ver reglas]"
}

REGLAS PARA instruction:
- Decirle al estudiante exactamente cuántas oraciones escribir (4-6 para A1-A2, 6-8 para B1+)
- DEBE mencionar explícitamente usar ${gp} como estructura principal
- Dar 3 elementos que deben incluir (personas, lugares, acciones específicas de "${t}")
- Usar lenguaje motivador y positivo
- Ejemplo: "Escribe 5 oraciones usando [gp] sobre [situación]. Incluye: [elemento 1], [elemento 2] y [elemento 3]."

REGLAS PARA theme:
- Situación real y específica relacionada con "${t}"
- Debe ser motivadora y relevante para alguien de 2026
- Debe requerir naturalmente el uso de ${gp}
- 1-2 oraciones máximo

DEVUELVE SOLO EL OBJETO data.`,

    // ── 11. SPACY MIRROR ─────────────────────────────────────────────
    spacy_mirror: HEADER + `GENERA el "data" para el registro en spacy_lessons (el cerebro del Tutor AI).

Este es el SISTEMA NERVIOSO del tutor Spacy para enseñar "${gp}" en "${t}".
Es diferente al conversation_spacy_prompt: aquí defines la PEDAGOGÍA PROFUNDA.

ESTRUCTURA REQUERIDA:
{
  "lesson_introduction": "[Bienvenida personalizada de Spacy — 2-3 frases, menciona el gp y el tema]",
  "learning_objectives": [array de 7-8 strings — las fases de la clase, termina con "Lección Completada"],
  "visual_gallery_cards": [array de 2-3 tarjetas],
  "ferrari_brain": {
    "pedagogy_flow": {
      "phase_1": { "name": "Setup & Context", "instruction_by_level": { "A1": "...", "B1_B2": "...", "C1_C2": "..." } },
      "phase_2": { "name": "Core Practice", "instruction_by_level": { "A1": "...", "B1_B2": "...", "C1_C2": "..." } },
      "phase_3": { "name": "Advanced Challenge", "instruction_by_level": { "A1": "...", "B1_B2": "...", "C1_C2": "..." } },
      "phase_4": { "name": "Real World Application", "instruction_by_level": { "A1": "...", "B1_B2": "...", "C1_C2": "..." } },
      "phase_5": { "name": "Victory Lap", "instruction": "CLOSURE: 1. Give a rating (1-5 stars). 2. Highlight ONE key correction. 3. End with high energy. TRIGGER: 'Lección Completada'." }
    },
    "gamification": {
      "metrics": ["Grammar Accuracy", "Fluency", "Contextual Use"],
      "success_criteria": "[Describe en 1 oración qué debe lograr el usuario para completar la lección]"
    }
  }
}

REGLAS PARA ferrari_brain.pedagogy_flow:
- Cada phase tiene instruction_by_level para A1, B1_B2, C1_C2
- A1: Traduce TODO al español con doble paréntesis: 'Hello ((Hola))'. Muy paso a paso.
- B1_B2: Contexto profesional/real. Sin español. Exige oraciones completas.
- C1_C2: Desafío avanzado. Matices. Roleplay de alta presión.
- Cada instrucción menciona ESPECÍFICAMENTE ${gp} y el contexto "${t}"
- TRIGGER CARD en phase 2 o 3: menciona el card_id de una visual_gallery_card

REGLAS PARA visual_gallery_cards:
{
  "trigger_topic": "[frase del learning_objective que la activa]",
  "card_id": "[ID único tipo 'V1-NombreCorto']",
  "title": "[Título de la tarjeta con emoji]",
  "content": "[Contenido: regla, diagrama textual, ❌✅ ejemplos específicos de ${gp} en ${t}]"
}

DEVUELVE SOLO EL OBJETO data.`,
  };

  return prompts[type] || `// Prompt para ${type} no definido`;
};

// ===================================================================
// CopyButton component
// ===================================================================
function CopyBtn({ text, color }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "5px 12px", borderRadius: 8, border: "none",
        cursor: "pointer",
        background: copied ? "#10b98122" : (color + "18"),
        color: copied ? "#10b981" : color,
        fontSize: 11, fontWeight: 700, fontFamily: "Inter,sans-serif",
        transition: "all .2s"
      }}>
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copiado" : "Copiar Prompt"}
    </button>
  );
}

// ===================================================================
// COMPONENTE PRINCIPAL
// ===================================================================
export default function IndividualPromptGenerator({ t, dark, HICOLOR, lessonId, lessonTopic, setLessonTopic, activeLevel }) {
  const [activeType,   setActiveType]   = useState("grammar_notes");
  const [grammarPoint, setGrammarPoint] = useState("");
  const [showPrompt,   setShowPrompt]   = useState(false);
  const [expanded,     setExpanded]     = useState(true);

  const meta = EX_TYPE_META[activeType];
  const promptText = buildPrompt(activeType, {
    topic:        lessonTopic,
    grammarPoint: grammarPoint,
    level:        activeLevel,
    lessonId:     lessonId,
  });

  const inputStyle = {
    width: "100%", background: "transparent", border: "none",
    outline: "none", padding: "8px 10px", color: t.tx,
    fontSize: 11, fontFamily: "Inter,sans-serif"
  };

  const fieldBoxStyle = {
    background: t.bg, borderRadius: 9,
    boxShadow: `inset -3px 3px 8px ${t.sd},inset 3px -3px 8px ${t.sl}`,
    padding: "1px 4px", marginBottom: 8
  };

  return (
    <div style={{
      background: t.bg, borderRadius: 20, padding: 16,
      boxShadow: t.nmd
    }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: expanded ? 14 : 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: dark ? "#a855f722" : "#7c3aed18",
          color: dark ? "#a855f7" : "#7c3aed"
        }}>
          <Brain size={14} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: t.tx }}>
            Divide y Vencerás · 11 Prompts Cambridge
          </div>
          <div style={{ fontSize: 9, color: t.tx2 }}>
            Un prompt por ejercicio — sin alucinaciones, sin mezcla de temas
          </div>
        </div>
        <button
          onClick={() => setExpanded(o => !o)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: t.tx2, display: "flex", alignItems: "center"
          }}>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {expanded && (
        <>
          {/* ── Inputs: Tema y Punto Gramatical ───────────────── */}
          <div style={{ marginBottom: 10 }}>
            <label style={{
              fontSize: 9, fontWeight: 700, color: t.tx2,
              textTransform: "uppercase", letterSpacing: ".1em",
              display: "block", marginBottom: 4
            }}>
              Tema de la lección
            </label>
            <div style={fieldBoxStyle}>
              <input
                type="text" value={lessonTopic}
                onChange={e => setLessonTopic(e.target.value)}
                placeholder="Ej: Greetings and Introductions, Parts of the House..."
                style={inputStyle} />
            </div>

            <label style={{
              fontSize: 9, fontWeight: 700, color: "#f59e0b",
              textTransform: "uppercase", letterSpacing: ".1em",
              display: "block", marginBottom: 4
            }}>
              ⚡ Punto Gramatical Central (la clave anti-alucinaciones)
            </label>
            <div style={{
              ...fieldBoxStyle,
              boxShadow: `inset -3px 3px 8px ${t.sd},inset 3px -3px 8px ${t.sl}, 0 0 0 1px #f59e0b33`
            }}>
              <input
                type="text" value={grammarPoint}
                onChange={e => setGrammarPoint(e.target.value)}
                placeholder="Ej: Present Perfect (have/has + participio), Articles (a/an/the)..."
                style={{ ...inputStyle, color: t.tx }} />
            </div>
            {!grammarPoint && (
              <div style={{ fontSize: 9, color: "#f59e0b", marginTop: 2 }}>
                ⚠️ Define el punto gramatical antes de generar. Es lo que unifica los 11 ejercicios.
              </div>
            )}
          </div>

          {/* ── Selector de Ejercicio ─────────────────────────── */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12
          }}>
            {EX_ORDER.map(type => {
              const m = EX_TYPE_META[type];
              const isActive = activeType === type;
              return (
                <button
                  key={type}
                  onClick={() => { setActiveType(type); setShowPrompt(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 3,
                    padding: "4px 8px", borderRadius: 8, border: "none",
                    cursor: "pointer", transition: "all .15s",
                    background: isActive ? (m.color + "22") : "transparent",
                    boxShadow: isActive
                      ? `${t.ism}, 0 0 0 1.5px ${m.color}88`
                      : `inset -1px 1px 3px ${t.sd},inset 1px -1px 3px ${t.sl}`,
                    color: isActive ? m.color : t.tx2,
                  }}>
                  <span style={{ fontSize: 12 }}>{m.emoji}</span>
                  <span style={{
                    fontSize: 8, fontWeight: isActive ? 800 : 500,
                    fontFamily: "JetBrains Mono,monospace"
                  }}>{m.pos}</span>
                  {isActive && (
                    <span style={{ fontSize: 8, fontWeight: 700, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Card del ejercicio activo ─────────────────────── */}
          <div style={{
            borderRadius: 12, overflow: "hidden",
            border: `1px solid ${meta.color}33`,
            background: dark ? (meta.color + "06") : (meta.color + "04"),
            marginBottom: 10
          }}>
            {/* Header de la card */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 12px",
              borderBottom: showPrompt ? `1px solid ${meta.color}22` : "none"
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, background: meta.color + "18",
                boxShadow: `0 0 0 1px ${meta.color}28`
              }}>
                {meta.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
                  Pos {meta.pos} · {meta.label}
                </div>
                <div style={{ fontSize: 9, color: t.tx2 }}>
                  {getExerciseHint(activeType)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <CopyBtn text={promptText} color={meta.color} />
                <button
                  onClick={() => setShowPrompt(o => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "5px 10px", borderRadius: 8,
                    border: `1px solid ${meta.color}33`,
                    cursor: "pointer",
                    background: showPrompt ? (meta.color + "18") : "transparent",
                    color: meta.color, fontSize: 10, fontWeight: 700,
                    fontFamily: "Inter,sans-serif"
                  }}>
                  <Sparkles size={10} />
                  {showPrompt ? "Ocultar" : "Ver Prompt"}
                </button>
              </div>
            </div>

            {/* Prompt expandido */}
            {showPrompt && (
              <div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 10px",
                  background: dark ? "#0d0d1a" : "#1e1e2e",
                  borderBottom: "1px solid rgba(255,255,255,.05)"
                }}>
                  <Terminal size={9} style={{ color: "rgba(255,255,255,.3)" }} />
                  <span style={{
                    fontSize: 8, fontFamily: "JetBrains Mono,monospace",
                    color: meta.color + "88"
                  }}>
                    prompt_{activeLevel}_{(lessonTopic || "topic").replace(/\s/g, "_").toLowerCase()}_{activeType}.txt
                  </span>
                </div>
                <div style={{
                  background: dark ? "#0d1117" : "#1e1e2e",
                  padding: "10px 12px", maxHeight: 300, overflowY: "auto"
                }}>
                  <pre style={{
                    margin: 0, fontSize: 9.5, lineHeight: 1.7,
                    fontFamily: "JetBrains Mono,monospace",
                    color: dark ? (meta.color + "cc") : "#4ade80",
                    whiteSpace: "pre-wrap"
                  }}>
                    {promptText}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* ── Guía de flujo ────────────────────────────────── */}
          <div style={{
            padding: "8px 10px", borderRadius: 10,
            background: t.bg, boxShadow: t.ixs,
            fontSize: 9, color: t.tx2, lineHeight: 1.7
          }}>
            <div style={{ fontWeight: 700, color: t.tx, marginBottom: 4, fontSize: 10 }}>
              Flujo de trabajo
            </div>
            {[
              "1. Crea la lección shell → obtienes el lesson_id real",
              "2. Escribe el Punto Gramatical Central (unifica todos los ejercicios)",
              "3. Selecciona ejercicio → Copia Prompt → Pega en Claude",
              "4. Claude devuelve el JSON del campo 'data' solamente",
              "5. Pega ese JSON en el ExerciseCard → botón Editar → Guardar",
              "6. Repite para los 11 (10 exercises + spacy mirror)",
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <span style={{ color: HICOLOR, flexShrink: 0, fontFamily: "JetBrains Mono,monospace" }}>→</span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Hints por tipo ───────────────────────────────────────────────
function getExerciseHint(type) {
  const hints = {
    conversation_spacy_prompt: "Plan de clase del tutor AI — learning_objectives + visual_gallery_cards",
    grammar_notes:             "Masterclass Cambridge — 4 points: regla, función, detalle, errores L1",
    quiz:                      "6 preguntas — answer = texto exacto de options, NUNCA índice",
    fill_in_the_blank:         "6 blanks con hint gramatical — before_blank + after_blank = oración completa",
    matching:                  "5 pares — solution NUNCA 1→1, todos los IDs deben existir en pairs/matches",
    listening:                 "Diálogo natural 8-12 líneas + 6 preguntas — audio_url puede ser PLACEHOLDER",
    pronunciation:             "10 items con IPA correcto — palabras → frases → oraciones completas",
    error_detection:           "7 oraciones (3 correctas + 4 incorrectas) — answer: 'Correct' o 'Incorrect' exacto",
    reading:                   "Texto 180-280 palabras con ${gp} en negritas + 6 preguntas comprensión",
    writing:                   "Instrucción detallada + theme motivador — el estudiante escribe libremente",
    spacy_mirror:              "Cerebro del tutor — ferrari_brain con pedagogy_flow adaptativo por nivel",
  };
  return hints[type] || "";
}